import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Button,
  Input,
  Badge,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { projectsAPI } from '../../helpers/api';
import { useIsMobile } from '../../helpers/useMediaQuery';
import SideModal from '../../components/Common/SideModal';

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  phone: string | null;
}

interface SitePersonnelEntry {
  id: string;
  assignedAt: string;
  personnel: Personnel;
}

interface Site {
  id: string;
  name: string;
  project: {
    name: string;
    city: { name: string };
  };
}

const SitePersonnel: React.FC = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [site, setSite] = useState<Site | null>(null);
  const [personnel, setPersonnel] = useState<SitePersonnelEntry[]>([]);
  const [unassignedPersonnel, setUnassignedPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    if (siteId) {
      fetchData();
    }
  }, [siteId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [siteRes, personnelRes, unassignedRes] = await Promise.all([
        projectsAPI.getSite(siteId!),
        projectsAPI.getSitePersonnel(siteId!),
        projectsAPI.getUnassignedPersonnel(siteId!),
      ]);
      setSite(siteRes.data);
      setPersonnel(personnelRes.data);
      setUnassignedPersonnel(unassignedRes.data);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (personnelId: string) => {
    try {
      setAdding(personnelId);
      await projectsAPI.assignPersonnelToSite(siteId!, personnelId);
      toast.success('Personel şantiyeye atandı');
      fetchData();
      setShowAddModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Atama başarısız');
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (personnelId: string) => {
    if (!window.confirm('Personeli şantiyeden çıkarmak istediğinize emin misiniz?')) return;

    try {
      setRemoving(personnelId);
      await projectsAPI.removePersonnelFromSite(siteId!, personnelId);
      toast.success('Personel şantiyeden çıkarıldı');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setRemoving(null);
    }
  };

  const filteredUnassigned = unassignedPersonnel.filter(
    (p) =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1">Şantiye Personeli</h4>
              {site && (
                <p className="text-muted mb-0">
                  {site.project.city.name} / {site.project.name} / {site.name}
                </p>
              )}
            </div>
            <div className="page-title-right d-flex gap-2">
              <Button color="primary" onClick={() => setShowAddModal(true)}>
                <i className="ri-user-add-line me-1"></i>
                Personel Ekle
              </Button>
              <Button color="outline-secondary" onClick={() => navigate(-1)}>
                <i className="ri-arrow-left-line me-1"></i>
                Geri
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">
                <i className="ri-team-line me-2"></i>
                Atanmış Personeller
                <Badge color="primary" className="ms-2">{personnel.length}</Badge>
              </h5>
            </CardHeader>
            <CardBody>
              {personnel.length === 0 ? (
                <div className="text-center py-4">
                  <i className="ri-user-unfollow-line text-muted" style={{ fontSize: '3rem' }}></i>
                  <p className="text-muted mt-2">Bu şantiyeye henüz personel atanmamış</p>
                  <Button color="primary" onClick={() => setShowAddModal(true)}>
                    <i className="ri-user-add-line me-1"></i>
                    Personel Ekle
                  </Button>
                </div>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {personnel.map((entry) => (
                    <div key={entry.id} className="admin-card-item">
                      {/* Card Header */}
                      <div className="admin-card-header">
                        <h6 className="admin-card-title">
                          {entry.personnel.firstName} {entry.personnel.lastName}
                        </h6>
                      </div>

                      {/* Card Body */}
                      <div className="admin-card-body">
                        <div className="admin-info-row">
                          <div className="admin-info-label">
                            <i className="ri-shield-user-line"></i>Görev
                          </div>
                          <div className="admin-info-value">
                            {entry.personnel.title || '-'}
                          </div>
                        </div>

                        <div className="admin-info-row">
                          <div className="admin-info-label">
                            <i className="ri-phone-line"></i>Telefon
                          </div>
                          <div className="admin-info-value">
                            {entry.personnel.phone || <span className="value-muted">-</span>}
                          </div>
                        </div>

                        <div className="admin-info-row">
                          <div className="admin-info-label">
                            <i className="ri-calendar-line"></i>Atanma Tarihi
                          </div>
                          <div className="admin-info-value">
                            {new Date(entry.assignedAt).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-danger"
                          style={{ flex: 1 }}
                          onClick={() => handleRemove(entry.personnel.id)}
                          disabled={removing === entry.personnel.id}
                        >
                          {removing === entry.personnel.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <i className="ri-user-unfollow-line"></i>
                              Şantiyeden Çıkar
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Personel</th>
                        <th>Görev</th>
                        <th>Telefon</th>
                        <th>Atanma Tarihi</th>
                        <th style={{ width: '100px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {personnel.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <strong>{entry.personnel.firstName} {entry.personnel.lastName}</strong>
                          </td>
                          <td>{entry.personnel.title || '-'}</td>
                          <td>{entry.personnel.phone || '-'}</td>
                          <td>
                            {new Date(entry.assignedAt).toLocaleDateString('tr-TR')}
                          </td>
                          <td>
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              onClick={() => handleRemove(entry.personnel.id)}
                              disabled={removing === entry.personnel.id}
                            >
                              {removing === entry.personnel.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="ri-user-unfollow-line"></i>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Personel Ekleme Modal */}
      <SideModal
        isOpen={showAddModal}
        toggle={() => setShowAddModal(false)}
        title="Personel Ekle"
        size="lg"
      >
        <Input
          type="text"
          placeholder="Personel ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-3"
        />

        {filteredUnassigned.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-0">
              {searchTerm ? 'Aramayla eşleşen personel bulunamadı' : 'Atanabilecek personel bulunmuyor'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="table-hover mb-0">
              <thead>
                <tr>
                  <th>Personel</th>
                  <th>Görev</th>
                  <th style={{ width: '100px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredUnassigned.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.firstName} {p.lastName}</strong>
                    </td>
                    <td>{p.title || '-'}</td>
                    <td>
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => handleAssign(p.id)}
                        disabled={adding === p.id}
                      >
                        {adding === p.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <i className="ri-add-line me-1"></i>
                            Ekle
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </SideModal>
    </>
  );
};

export default SitePersonnel;
